import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseUndoableStatusChangeParams, UseUndoableStatusChangeReturn } from './useUndoableStatusChange.types';

export type { UseUndoableStatusChangeParams, UseUndoableStatusChangeReturn } from './useUndoableStatusChange.types';

export const UNDO_DURATION_SECONDS = 30;
const UNDO_DURATION_MS = UNDO_DURATION_SECONDS * 1000;
const STORAGE_KEY = 'valuation-status-undo-entries';

interface UndoEntry {
  valuationId: string;
  previousStatusId: string;
  newStatusId: string;
  expiresAt: number;
}

const readEntries = (): UndoEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UndoEntry[]) : [];
  } catch {
    return [];
  }
};

const getValidEntry = (valuationId: string): UndoEntry | undefined =>
  readEntries().find(e => e.valuationId === valuationId && e.expiresAt > Date.now());

const saveEntry = (entry: UndoEntry) => {
  const filtered = readEntries().filter(e => e.valuationId !== entry.valuationId && e.expiresAt > Date.now());
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...filtered, entry]));
};

const removeEntry = (valuationId: string) => {
  const filtered = readEntries().filter(e => e.valuationId !== valuationId);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
};

export const useUndoableStatusChange = ({
  valuationId,
  onCommit,
  onUndo,
  onBeforeUndo,
}: UseUndoableStatusChangeParams): UseUndoableStatusChangeReturn => {
  const [isUndoPending, setIsUndoPending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [pendingPreviousStatusId, setPendingPreviousStatusId] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsUndoPending(false);
    setSecondsLeft(0);
    setPendingPreviousStatusId(null);
  }, []);

  const startCountdown = useCallback(
    (expiresAt: number, prevId: string) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      setPendingPreviousStatusId(prevId);
      setIsUndoPending(true);

      const tick = () => {
        const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
        if (remaining <= 0) {
          clearCountdown();
          if (valuationId) {
            removeEntry(valuationId);
          }
        } else {
          setSecondsLeft(remaining);
        }
      };

      tick();
      intervalRef.current = setInterval(tick, 1000);
    },
    [valuationId, clearCountdown]
  );

  // On mount or valuationId change: restore any pending undo from localStorage
  useEffect(() => {
    if (valuationId) {
      const entry = getValidEntry(valuationId);
      if (entry) {
        startCountdown(entry.expiresAt, entry.previousStatusId);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [valuationId, startCountdown]);

  const triggerChange = useCallback(
    async (newStatusId: string, previousStatusId: string) => {
      if (!valuationId) {
        return;
      }

      const expiresAt = Date.now() + UNDO_DURATION_MS;
      saveEntry({ valuationId, previousStatusId, newStatusId, expiresAt });
      startCountdown(expiresAt, previousStatusId);

      // Commit runs in the background so undo UI appears immediately.
      // Callers should handle errors inside onCommit (e.g. rollback + snackbar).
      onCommit(newStatusId);
    },
    [valuationId, onCommit, startCountdown]
  );

  const dismissUndo = useCallback(() => {
    if (valuationId) {
      removeEntry(valuationId);
    }
    clearCountdown();
  }, [valuationId, clearCountdown]);

  const handleUndo = useCallback(async () => {
    if (!valuationId || !pendingPreviousStatusId) {
      return;
    }
    const prevId = pendingPreviousStatusId;
    onBeforeUndo?.(prevId);
    clearCountdown();
    removeEntry(valuationId);
    await onUndo(prevId);
  }, [valuationId, pendingPreviousStatusId, onBeforeUndo, onUndo, clearCountdown]);

  return { triggerChange, handleUndo, dismissUndo, isUndoPending, secondsLeft };
};
